<template>
  <div
    ref="root"
    class="gallery size-full"
    :class="{
      'gallery-rx': layout === 'rx',
      'lg:grow': layout === 'rx',
      'lg:flex': layout === 'rx',
      'mix-blend-multiply':
        layout === 'rx' && !isOpen && !hasChangedOrientation,
    }"
  >
    <div
      ref="background"
      class="w-full"
      :class="[
        isOpen
          ? 'is-open fixed left-0 top-0 z-50 m-0 h-screen w-screen bg-white'
          : 'relative bg-transparent',
      ]"
    >
      <HorizontalItemsScroller
        class="size-full"
        :loop="loop"
        :dots="true"
        :lock="lock"
        :is-fullscreen="isOpen"
        @change="onSlideChange"
        @before-change="onBeforeSlideChange"
        @scroll-start="onScrollStart"
        @scroll-end="onScrollEnd"
      >
        <ScrollerItem
          v-for="(image, index) in images"
          :key="'slide-' + index"
          v-slot="slotProps"
          :data-index="index"
          class="item relative flex aspect-[1920/1075] size-full shrink-0 grow-0 basis-full snap-start items-stretch justify-center"
          :class="{
            'is-zoom': index === activeImageIndex && isOpen && isZoom,
            'cursor-zoom-in': index === activeImageIndex && !isZoom,
            'cursor-grab':
              index === activeImageIndex && isOpen && isZoom && !isGrabbing,
            'cursor-grabbing':
              index === activeImageIndex && isOpen && isZoom && isGrabbing,
            'z-10': index === activeImageIndex && isOpen,
          }"
          @click="toggleZoom"
          @mousedown="startDragging"
          @mouseup="stopDragging"
          @mousemove="drag"
          @touchstart="startDragging"
          @touchend="stopDragging"
          @touchmove="drag"
        >
          <NuxtPicture
            :alt="productName"
            quality="85"
            :width="1920"
            :height="1075"
            :background="'FFFFFF'"
            :src="image.hash"
            :modifiers="{ brightness: 1 }"
            format="avif,webp"
            placeholder="[50, 25, 50, 5]"
            :img-attrs="
              !slotProps.clone && index === 0
                ? { fetchPriority: fetchPriority }
                : undefined
            "
            :preload="
              fetchPriority === 'high' && !slotProps.clone && index === 0
                ? true
                : false
            "
            :loading="!slotProps.clone && index === 0 ? loading : 'lazy'"
            sizes="sm:200vw md:200vw lg:200vw lg:200vw 2lg:200vw 3lg:200vw 4lg:200vw"
            provider="fim"
            class="picture block aspect-[1920/1075] size-full"
            :class="[
              isImageType(ProductImageType.MODEL, image) ||
              getAttributeValue(image.attributes, 'imageBackground') === 'grey'
                ? 'picture-cover'
                : 'picture-contain',
            ]"
          />
        </ScrollerItem>
      </HorizontalItemsScroller>
      <FimButton
        type="secondary"
        no-padding
        class="absolute right-2 top-2 z-10 gap-0 p-2"
        :class="!isOpen && 'hidden'"
        :aria-label="$t('gallery.close')"
        @click="close()"
      >
        <template #icon>
          <LazyIconFielmannClose class="size-6 text-black" />
        </template>
      </FimButton>
    </div>
  </div>
</template>

<script setup lang="ts">
import { getAttributeValue } from '@scayle/storefront-nuxt'
import { type State, usePinch } from '@vueuse/gesture'
import { debounce } from 'throttle-debounce'
import FimButton from '~/components/ui/buttons/FimButton.vue'
import HorizontalItemsScroller from '~/components/ui/sliders/HorizontalItemsScroller.vue'
import useUiState from '~/composables/ui/useUiState'

/**
 * Clamp number between two values
 * @param {number} num Number which should be clamped
 * @param {number} min Min value
 * @param {number} max Max value
 * @returns Clamped number between min & max
 */
const clamp = (num: number, min: number, max: number): number =>
  Math.min(Math.max(num, min), max)

const props = defineProps({
  modelValue: {
    type: Number as PropType<number>,
    required: true,
  },
  loop: {
    type: Boolean as PropType<boolean>,
    default: false,
  },
  layout: {
    type: String as PropType<'rx' | ''>,
    default: '',
  },
  images: {
    type: Array as PropType<any[]>,
    default: undefined,
  },
  productName: {
    type: String as PropType<string>,
    default: undefined,
  },
  fetchPriority: {
    type: String as PropType<'high' | 'low' | 'auto'>,
    default: 'high',
  },
  loading: {
    type: String as PropType<'lazy' | 'eager'>,
    default: 'eager',
  },
})

const emit = defineEmits(['open', 'close', 'update:modelValue'])

const { setOverlayState } = useUiState()
const root = ref<HTMLElement>()
const background = ref<HTMLElement>()
const isOpen = ref(false)
const isGrabbing = ref(false)
const isDragging = ref(false)
const isZoom = ref(false)
const activeImageIndex = computed({
  get(): number {
    return props.modelValue
  },
  set(newValue: number): void {
    emit('update:modelValue', newValue)
  },
})
const rect = ref<DOMRect>()
const scaleFactor = ref<number>(1)
const dragStartX = ref<number>(0)
const dragStartY = ref<number>(0)
const imageX = ref<number>(0)
const imageY = ref<number>(0)
const isBusy = ref(false)
const lock = ref(false)

const pinchHandler = (state: State['pinch']) => {
  const [value] = state.vdva
  isDragging.value = false

  if (isBusy.value || !isOpen.value) {
    return state.event?.preventDefault()
  } else if (value > 0 && !isZoom.value) {
    toggleZoom(state.event as PointerEvent)
    state.cancel()
  } else if (value < 0 && isZoom.value) {
    isZoom.value = false
    state.cancel()
  }
  state.event?.preventDefault()
}

// Composable usage
usePinch(pinchHandler, {
  domTarget: background,
  eventOptions: {
    passive: false,
  },
})

const onScrollStart = () => {
  isBusy.value = true
}
const onScrollEnd = () => {
  isBusy.value = false
}

const onBeforeSlideChange = () => {
  isZoom.value = false
}
const onSlideChange = (event: any) => {
  isZoom.value = false
  isDragging.value = false
  activeImageIndex.value = event?.index
}

/**
 * Zoom: open layer
 */
const open = () => {
  lock.value = true

  // Set width & height of the root component so the content on the page
  // doesn't jump around when entering layer mode
  // wait until layout is done and recalculate
  setTimeout(() => {
    if (root.value) {
      const rect = background?.value?.getBoundingClientRect()
      root.value.style.setProperty(
        '--width',
        `${Math.ceil(rect?.width ?? 0)}px`,
      )
      root.value.style.setProperty(
        '--height',
        `${Math.ceil(rect?.height ?? 0)}px`,
      )
      lock.value = false
    }
  }, 10)

  isOpen.value = true
  setOverlayState(true)
  emit('open', { target: root.value })
}

/**
 * Zoom: close layer
 */
const close = () => {
  isZoom.value = false
  isOpen.value = false
  isDragging.value = false
  lock.value = true
  setOverlayState(false)

  // reset width/height
  if (root.value) {
    root.value.style.removeProperty('--width')
    root.value.style.removeProperty('--height')
  }
  setTimeout(() => {
    lock.value = false
  }, 50)

  emit('close', { target: root.value })
}

// Need to expose open method because it's invoked from parent ProductDetailGallery
defineExpose({ open })

/**
 * Toggle zoom state and compute css custom properties for scale & translate
 */
const toggleZoom = debounce(
  250,
  (event: PointerEvent | MouseEvent) => {
    if (isBusy.value) {
      return
    }
    if (isDragging.value) {
      return
    }

    if (!isOpen.value) {
      return open()
    }

    isZoom.value = !isZoom.value

    const minScale = 1.5
    const maxScale = 3

    if (isZoom.value && event.target) {
      const el = event.target as HTMLElement
      // Store the boundingbox in ref so we don't need to compute it again when dragging
      rect.value = el.getBoundingClientRect()
      const image = el.closest('img') || el.querySelector('img')
      let naturalWidth = image?.naturalWidth ?? rect.value.width * minScale

      // try to get width from url
      if (image?.currentSrc) {
        const url = new URL(image?.currentSrc)
        const w = url.searchParams.get('width') || `${naturalWidth}`
        if (w) {
          naturalWidth = parseInt(w, 10)
        }
      }

      const width = rect.value.width
      const height = rect.value.height

      // Scalefactor should at least be minScale but can be higher if the original image is big enough
      // Stored in ref so we don't need to compute it again when dragging
      scaleFactor.value = clamp(naturalWidth / width, minScale, maxScale)
      const { clientX, clientY } = getPointerPosition(event)
      const newWidth = width * scaleFactor.value
      const newHeight = height * scaleFactor.value

      // Compute normalized translate factors from -1 to 1 (-1/1 is at the edge, 0 is in the center)
      const factorX = clamp((width / 2 - clientX) / (width / 2), -1, 1)
      const factorY = clamp((height / 2 - clientY) / (height / 2), -1, 1)

      // Compute the amount we need to translate the image to show the clicked area when zooming
      const x = ((newWidth - width) / 2) * factorX
      const y = ((newHeight - height) / 2) * factorY

      el.style.setProperty('--scale', `${scaleFactor.value}`)
      el.style.setProperty('--x', `${x}`)
      el.style.setProperty('--y', `${y}`)
    }
  },
  { atBegin: true },
)

/**
 * Get pointer/touch position based on event type
 */
const getPointerPosition = (event: any) => {
  if (event?.touches) {
    return {
      clientX: event.touches[0].clientX,
      clientY: event.touches[0].clientY,
    }
  }
  return {
    clientX: event.clientX,
    clientY: event.clientY,
  }
}

const startDragging = (event: PointerEvent | TouchEvent | MouseEvent) => {
  if (!isOpen.value || !isZoom.value) {
    isDragging.value = false
    return
  }
  // @ts-expect-error touches is not available on PointerEvent
  if ((event?.touches?.length ?? 0) > 1) {
    return
  }

  const el = event.target as HTMLElement
  const { clientX, clientY } = getPointerPosition(event)
  isGrabbing.value = true
  isDragging.value = false
  dragStartX.value = clientX
  dragStartY.value = clientY
  // Store current image translate values
  imageX.value = parseFloat(el.style.getPropertyValue('--x'))
  imageY.value = parseFloat(el.style.getPropertyValue('--y'))
}

const stopDragging = () => {
  isGrabbing.value = false
}

const drag = (event: PointerEvent | TouchEvent | MouseEvent) => {
  if (!isGrabbing.value) {
    return
  }

  event.preventDefault()
  const el = event.target as HTMLElement
  if (!rect.value) {
    return { x: 0, y: 0, scale: 1 }
  }

  const { clientX, clientY } = getPointerPosition(event)
  const width = rect.value.width
  const height = rect.value.height
  const newWidth = width * (scaleFactor?.value ?? 1)
  const newHeight = height * (scaleFactor?.value ?? 1)

  const offsetX = clientX - (dragStartX?.value ?? 0)
  const offsetY = clientY - (dragStartY?.value ?? 0)

  isDragging.value = Math.abs(offsetX) > 5 || Math.abs(offsetY) > 5

  const thresholdX = (newWidth - width) / 2
  const thresholdY = (newHeight - height) / 2

  const x = clamp(imageX.value + offsetX, -1 * thresholdX, thresholdX)
  const y = clamp(imageY.value + offsetY, -1 * thresholdY, thresholdY)

  el.style.setProperty('--x', `${x}`)
  el.style.setProperty('--y', `${y}`)
}

// On iPad when device is rotated mix-blend-mode stops working. Seems to be
// related to overflow-x-scroll + flex class on scroller within
// HorizontalItemsScroller. This solves the issue by forcing Safari to
// re-render the element after every device orientation change.
// Perhaps a better non-hacky solution can be found?
// https://aboutyou.atlassian.net/browse/SCFIM-870
const hasChangedOrientation = ref(false)
onMounted(() => {
  // Once (if) all PDP pages get the new design this prop can be removed
  if (props.layout !== 'rx') {
    return
  }
  const mediaQueryList = window.matchMedia('(orientation: portrait)')
  function handleOrientationChange() {
    lock.value = true // make sure slider does not move
    hasChangedOrientation.value = true
    setTimeout(() => {
      hasChangedOrientation.value = false
      lock.value = false
    }, 100)
  }
  mediaQueryList.addEventListener('change', handleOrientationChange)
})
</script>

<style lang="scss" scoped>
/* stylelint-disable rule-empty-line-before */
.gallery {
  width: var(--width, auto);
  height: var(--height, auto);
  touch-action: none;
}

.item {
  flex: 0 0 100%;
  min-height: 10px;
  backface-visibility: hidden;
  overflow: hidden;
}

.item :deep(picture) {
  display: flex;
  justify-content: center;
}

.item :deep(img) {
  transition-property: transform;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
  transition-duration: 150ms;
  pointer-events: none;
  user-select: none;
}

.is-open .item :deep(img) {
  max-width: calc(100vw - 7rem);
}

.item.cursor-grabbing :deep(img) {
  transition: none;
}

.item.is-zoom :deep(img) {
  transform: translate3d(calc(var(--x, 0) * 1px), calc(var(--y, 0) * 1px), 0)
    scale3d(var(--scale, 1), var(--scale, 1), 1);
}

.gallery :deep(.slider-btn-prev) {
  background-color: transparent;
  left: -0.75rem;
  /* stylelint-disable-next-line */
  @screen xl {
    left: 1rem;
  }
}

.gallery :deep(.slider-btn-next) {
  background-color: transparent;
  right: -0.75rem;
  /* stylelint-disable-next-line */
  @screen xl {
    right: 1rem;
  }
}

.is-open :deep(.slider-btn-prev),
.is-open :deep(.slider-btn-next) {
  padding: 0.5rem;
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 20;
  /* stylelint-disable-next-line plugin/no-low-performance-animation-properties */
  transition: border-color 100ms ease-out;
  border: 1px solid rgba(215 215 217 / 1);
  background-color: #ffffff;
}

.is-open :deep(.slider-btn-prev) {
  left: 0.5rem;
}

.is-open :deep(.slider-btn-next) {
  right: 0.5rem;
}

.is-open :deep(.slider-btn-prev:hover),
.is-open :deep(.slider-btn-next:hover) {
  border-color: rgb(147 149 153 / 1);
}

.is-open :deep(.slider-dots) {
  margin-top: -2.5rem;
  z-index: 20;
}

/* Temporary (hopefully) layout tweaks until images bgs are handled on API */
.gallery-rx :deep(.wrapper::before),
.gallery-rx :deep(.wrapper::after) {
  display: none;
}
</style>
